import { Pulse } from '@/components/activity/activity-panel'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { UserActivityTime } from '@/components/user-activity-time'
import { cn } from '@/lib/utils'
import type {
  OrgActivityAttentionUser,
  OrgActivityTopUser
} from '@/models/IActivity'
import { LAST_ACTIVITY_HINT, LAST_LOGIN_HINT } from '@/utils/user-activity'

const ROW_CLASS =
  'cursor-pointer border-border/30 hover:bg-muted/30 focus-visible:bg-muted/30 focus-visible:outline-none'
const HEAD_ROW_CLASS = 'border-border/30 hover:bg-transparent'

const UserCell = ({
  name,
  email,
  userId
}: {
  name: string | null
  email: string | null
  userId: string
}) => (
  <div className="min-w-0 leading-tight">
    <div className="truncate font-medium">{name || email || userId}</div>
    <div className="truncate text-xs text-muted-foreground/70">
      {name ? email || userId : email ? userId : ''}
    </div>
  </div>
)

const rowProps = (onSelect: () => void) => ({
  className: ROW_CLASS,
  onClick: onSelect,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect()
    }
  },
  role: 'button',
  tabIndex: 0
})

const LoadingRows = ({ cols }: { cols: number }) => (
  <>
    {Array.from({ length: 4 }, (_, i) => (
      <TableRow className={HEAD_ROW_CLASS} key={i}>
        {Array.from({ length: cols }, (__, c) => (
          <TableCell key={c}>
            <Pulse className="h-4 w-full" />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
)

const EmptyRow = ({ cols, text }: { cols: number; text: string }) => (
  <TableRow className={HEAD_ROW_CLASS}>
    <TableCell
      className="py-6 text-center text-sm text-muted-foreground/70"
      colSpan={cols}
    >
      {text}
    </TableCell>
  </TableRow>
)

type TopUsersTableProps = {
  users?: OrgActivityTopUser[]
  /** number of days in the range, for the active days bar */
  rangeDays: number
  isLoading?: boolean
  onSelectUser: (userId: string) => void
}

export const TopUsersTable = ({
  users,
  rangeDays,
  isLoading,
  onSelectUser
}: TopUsersTableProps) => (
  <Table>
    <TableHeader>
      <TableRow className={HEAD_ROW_CLASS}>
        <TableHead className="w-8" />
        <TableHead>User</TableHead>
        <TableHead>Active days</TableHead>
        <TableHead>Last activity</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {isLoading ? (
        <LoadingRows cols={4} />
      ) : !users?.length ? (
        <EmptyRow cols={4} text="No active users in this period." />
      ) : (
        users.map((user, i) => (
          <TableRow
            key={user.user_id}
            {...rowProps(() => onSelectUser(user.user_id))}
          >
            <TableCell className="text-muted-foreground/60 tabular-nums">
              {i + 1}
            </TableCell>
            <TableCell className="max-w-[220px]">
              <UserCell
                email={user.email}
                name={user.name}
                userId={user.user_id}
              />
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted/50">
                  <div
                    className="h-full rounded-full bg-secondary"
                    style={{
                      width: `${Math.min(
                        100,
                        (user.active_days / Math.max(rangeDays, 1)) * 100
                      )}%`
                    }}
                  />
                </div>
                <span className="whitespace-nowrap text-sm tabular-nums">
                  {user.active_days}
                  <span className="text-muted-foreground/60">
                    {' '}
                    / {rangeDays}
                  </span>
                </span>
              </div>
            </TableCell>
            <TableCell>
              <UserActivityTime
                hint={LAST_ACTIVITY_HINT}
                value={user.last_seen_at}
              />
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
)

const REASON_LABELS: Record<OrgActivityAttentionUser['reason'], string> = {
  dormant: 'Dormant',
  never_seen: 'Never seen'
}

export const ReasonChip = ({
  reason
}: {
  reason: OrgActivityAttentionUser['reason']
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold',
      reason === 'dormant'
        ? 'bg-amber-500/15 text-amber-400'
        : 'bg-muted/60 text-muted-foreground/80'
    )}
  >
    {REASON_LABELS[reason]}
  </span>
)

const formatMemberSince = (value: string | null) => {
  if (!value) return '—'

  const date = new Date(value)

  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

type AttentionTableProps = {
  users?: OrgActivityAttentionUser[]
  isLoading?: boolean
  onSelectUser: (userId: string) => void
}

export const AttentionTable = ({
  users,
  isLoading,
  onSelectUser
}: AttentionTableProps) => (
  <Table>
    <TableHeader>
      <TableRow className={HEAD_ROW_CLASS}>
        <TableHead>User</TableHead>
        <TableHead>Last login</TableHead>
        <TableHead>Member since</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {isLoading ? (
        <LoadingRows cols={3} />
      ) : !users?.length ? (
        <EmptyRow
          cols={3}
          text="Every member was active in the last 30 days."
        />
      ) : (
        users.map((user) => (
          <TableRow
            key={user.user_id}
            {...rowProps(() => onSelectUser(user.user_id))}
          >
            <TableCell className="max-w-[260px]">
              <div className="flex items-start gap-2">
                <UserCell
                  email={user.email}
                  name={user.name}
                  userId={user.user_id}
                />
                <ReasonChip reason={user.reason} />
              </div>
            </TableCell>
            <TableCell>
              <UserActivityTime
                hint={LAST_LOGIN_HINT}
                value={user.last_login_at}
              />
            </TableCell>
            <TableCell className="whitespace-nowrap text-sm">
              {formatMemberSince(user.created_at)}
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
)
